import React from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { Loader2 } from 'lucide-react';
import { cn } from 'src/lib/utils';
import { Badge } from 'src/components/ui/badge';
import { RequestResponse } from 'src/lib/services/api/request-api';
import { useTranslation } from 'react-i18next';
import { requestStatusTranslationsMap } from 'src/features/requests/request.helper';

const requestBadgeVariants = cva('tw-rounded-lg tw-border-transparent tw-font-medium', {
  variants: {
    variant: {
      draft: 'tw-bg-blue-300 tw-text-blue-500',
      uploading: 'tw-bg-amber-200 tw-text-amber-900',
      upload_failed: 'tw-bg-red-500 tw-text-white',
      queued: 'tw-bg-neutral-200 tw-text-neutral-700',
      currently_editing: 'tw-bg-yellow-300 tw-text-yellow-900',
      ready_for_review: 'tw-bg-red-200 tw-text-red-900',
      needs_attention: 'tw-bg-amber-200 tw-text-amber-900',
      needs_reedit: 'tw-bg-red-200 tw-text-red-900',
      delivered: 'tw-bg-purple-200 tw-text-brand',
      complete: 'tw-bg-green-200 tw-text-green-900',
      archived: 'tw-bg-orange-200 tw-text-orange-900',
      unknown: 'tw-bg-neutral-200 tw-text-neutral-700',
    } as { [key: RequestResponse['status'] | string]: string },
    size: {
      sm: 'tw-px-2 tw-py-1 tw-text-sm',
      default: 'tw-px-4 tw-py-1 tw-text-base',
    },
    // 'table': the Requests table pill, styled like Admin's (#2756) - 8px
    // corners and a purple "Ready for review". Elsewhere Space keeps its look.
    look: {
      default: '',
      table: 'tw-rounded-[0.5rem]',
    },
  },
  compoundVariants: [{ variant: 'delivered', look: 'table', class: 'tw-text-purple-500' }],
  defaultVariants: {
    variant: 'unknown',
    size: 'default',
    look: 'default',
  },
});

type RequestStatusBadgeVariantProps = VariantProps<typeof requestBadgeVariants>;

export type RequestStatusBadgeProps = React.HTMLAttributes<HTMLDivElement> &
  Omit<RequestStatusBadgeVariantProps, 'variant'> & {
    variant?: string | RequestStatusBadgeVariantProps['variant'];
    iconPrefix?: React.ReactNode;
  };

const variants: RequestStatusBadgeProps['variant'][] = [
  'draft',
  'uploading',
  'upload_failed',
  'queued',
  'currently_editing',
  'ready_for_review',
  'needs_attention',
  'needs_reedit',
  'delivered',
  'complete',
  'archived',
];

function RequestStatusBadge({
  className,
  size,
  look,
  variant,
  iconPrefix,
  children,
  ...props
}: RequestStatusBadgeProps) {
  const { t } = useTranslation();

  const label = children ?? t(requestStatusTranslationsMap(`${variant}`));

  return (
    <Badge
      variant={'unset'}
      className={cn(
        requestBadgeVariants({ size, look, variant: variants?.includes(variant) ? variant : 'unknown' }),
        className,
      )}
      {...props}
    >
      {iconPrefix ? (
        <span className={'tw-inline-flex tw-items-center tw-gap-1'}>
          {iconPrefix}
          {label}
        </span>
      ) : (
        label
      )}
    </Badge>
  );
}

// Shared "pill with leading spinner" used when an upload is in flight on
// either the `uploading` or `queued` rows. Thin wrapper over
// RequestStatusBadge's `iconPrefix` slot — kept as a named component so
// call sites read intent-first ("loader pill") rather than reconstructing
// the spinner JSX inline.
type StatusBadgeWithLoaderProps = Omit<RequestStatusBadgeProps, 'children' | 'iconPrefix'>;

function StatusBadgeWithLoader(props: StatusBadgeWithLoaderProps) {
  return (
    <RequestStatusBadge
      {...props}
      iconPrefix={
        <Loader2 size={12} className={'tw-shrink-0 tw-animate-spin'} aria-hidden={'true'} />
      }
    />
  );
}

export { RequestStatusBadge, StatusBadgeWithLoader };
